import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel } from '@material-ui/core';
import PropTypes from 'prop-types';

import Modal from './Modal';

// Constants
import { T, ALERT_DETAILS, onColor } from '../constants/tokens';

/**
 * Confirms setting — or clearing — the school-wide status.
 *
 * The confirm button takes the status colour, so the thing you are about to
 * do is the loudest element in the dialog.
 *
 * A status that alerts the whole school can be run as a drill: every device
 * shows it as normal, but no red alert texts are sent and no emergency call is
 * placed. A drill cannot start while a real alert is in force — the server
 * refuses it too — so the option is disabled then, with the reason.
 */
export default function ConfirmStatusModal({ closeModal, showModal, setStatus, desiredStatus, clearStatus, realAlertInForce = false }) {
  const [isDrill, setIsDrill] = useState(false);

  // Every opening starts as a real alert. A drill is always a deliberate choice.
  useEffect(() => {
    if (showModal) setIsDrill(false);
  }, [showModal, desiredStatus]);

  const setting = desiredStatus != null;
  const color = setting ? desiredStatus.getColor() : null;

  // AlertType has no `name`; this read undefined and rendered
  // "Confirm undefined Alert to entire school?".
  const statusName = setting ? desiredStatus.getDashboardStatus() : null;
  const canDrill = setting && desiredStatus.getShouldAlertSchool?.() === true;
  const drill = canDrill && isDrill && !realAlertInForce;
  const dispatches = setting && statusName === 'Emergency';

  const key = setting
    ? Object.keys(ALERT_DETAILS).find(
      (k) => ALERT_DETAILS[k].message?.startsWith(statusName)
    )
    : null;
  const action = key ? ALERT_DETAILS[key].action : null;

  const body = !setting
    ? 'Every room returns to Normal and badges stop showing an alert.'
    : [
      'Every badge in the school will show this alert.',
      action ? action + '.' : null,
      drill
        ? 'As a drill, no red alert texts are sent and emergency services are not called.'
        // A school can switch the automatic call off, so the call is not promised.
        : (dispatches ? 'Red alert contacts are texted, and emergency services are called unless automatic calls are switched off for this school.' : null)
    ].filter(Boolean).join(' ');

  return (
    <Modal
      open={showModal}
      onClose={closeModal}
      eyebrow={setting ? (drill ? 'Confirm drill' : 'Confirm alert') : 'Confirm'}
      dotColor={setting ? color.main : T.mutedSoft}
      title={setting
        ? (drill ? 'Start ' + (/^[AEIOU]/i.test(statusName) ? 'an ' : 'a ') + statusName + ' drill for the whole school?' : 'Set ' + statusName + ' for the whole school?')
        : 'Clear every room status?'}
      body={body}
      actions={
        <>
          <Button variant='outlined' onClick={closeModal}>Cancel</Button>
          {setting ?
            <Button
              variant='contained'
              onClick={() => setStatus(desiredStatus, drill)}
              style={{ background: color.main, color: onColor(color.main) }}
            >
              {drill ? 'Start ' + statusName + ' drill' : 'Set ' + statusName}
            </Button>
            :
            <Button variant='contained' color='primary' onClick={() => clearStatus()}>
              Clear all
            </Button>}
        </>
      }
    >
      {canDrill &&
        <div
          style={{
            margin: '0 0 24px',
            padding: '12px 16px',
            border: '1px solid ' + T.hairline,
            borderRadius: 6,
            background: T.surfaceSoft
          }}
        >
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Checkbox
                checked={drill}
                disabled={realAlertInForce}
                onChange={(e) => setIsDrill(e.target.checked)}
                inputProps={{ 'aria-describedby': 'drill-help' }}
                style={{ padding: '0 10px 0 0' }}
              />
            }
            label={<span style={{ fontSize: 14, fontWeight: 500, color: T.ink }}>Run as a drill</span>}
          />
          <div id='drill-help' style={{ fontSize: 13, lineHeight: '18px', color: T.body, marginTop: 4, marginLeft: 34 }}>
            {realAlertInForce
              ? 'Not available while a real alert is in force. Clear it first.'
              : 'Devices show the alert as normal. No texts are sent and emergency services are not called. A real alert raised during the drill ends it.'}
          </div>
        </div>}
    </Modal>
  );
}

ConfirmStatusModal.propTypes = {
  showModal: PropTypes.bool,
  closeModal: PropTypes.func,
  setStatus: PropTypes.func,
  desiredStatus: PropTypes.object,
  clearStatus: PropTypes.func,
  realAlertInForce: PropTypes.bool
};
